import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import Sidebar from "../../layout/Sidebar";
import Navbar from "../../layout/Navbar";
import {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from "../../data/content";

const Dashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const handleSidebarToggle = () => setSidebarOpen((open) => !open);
  const handleSidebarClose = () => setSidebarOpen(false);
  const sidebarWidth = sidebarOpen
    ? SIDEBAR_WIDTH_EXPANDED
    : SIDEBAR_WIDTH_COLLAPSED;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Line chart data
  const salesData = [
    { month: "Jan", achieved: 400, target: 300 },
    { month: "Feb", achieved: 500, target: 450 },
    { month: "Mar", achieved: 700, target: 600 },
    { month: "Apr", achieved: 650, target: 600 },
    { month: "May", achieved: 550, target: 500 },
  ];

  // Pie chart data
  const orderData = [
    { name: "Completed", value: 25, color: "#4e73df" },
    { name: "On Hold", value: 25, color: "#6f42c1" },
    { name: "On Progress", value: 25, color: "#f6c23e" },
    { name: "Pending", value: 18, color: "#e74a3b" },
  ];

  // Donut chart data
  const expenseData = [
    { name: "Administrative", value: 10, color: "#e74a3b" },
    { name: "Equipment", value: 20, color: "#6f42c1" },
    { name: "Labor", value: 30, color: "#f6c23e" },
    { name: "Material", value: 40, color: "#4e73df" },
  ];

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "#f5f7fb",
        display: "flex",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={handleSidebarClose} />
      <Box>
        <Navbar onMenuClick={handleSidebarToggle} sidebarOpen={sidebarOpen} />

        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            width: `calc(100vw - ${sidebarWidth}px)`,
            minHeight: "calc( 100vh - 56px)",
          }}
        >
          <Typography variant="h5" fontWeight="bold" mb={3}>
            Dashboard
          </Typography>

          <Grid container spacing={3}>
            {/* Sales Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  p: 3,
                  height: "100%",
                  width: { xs: "300px", md: "400px" },
                }}
              >
                <Typography variant="subtitle1">Total Sales:</Typography>
                <Typography variant="h5" fontWeight="bold">
                  $4,500
                </Typography>
                <Typography variant="body2" color="green">
                  ↑ 12% from last month
                </Typography>

                <ResponsiveContainer width="100%" height={isMobile ? 160 : 200}>
                  <LineChart data={salesData}>
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="achieved"
                      stroke="#e74a3b"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="target"
                      stroke="#6f42c1"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Grid>

            {/* Orders Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  p: 3,
                  height: "100%",
                  width: { xs: "300px", md: "400px" },
                }}
              >
                <Typography variant="subtitle1">Sales Overview:</Typography>
                <Typography variant="h6" fontWeight="bold">
                  +24 Orders
                </Typography>
                <Button size="small" sx={{ mt: 1, background: "#eaf2ff" }}>
                  This Month
                </Button>

                <ResponsiveContainer width="100%" height={isMobile ? 160 : 200}>
                  <PieChart>
                    <Pie
                      data={orderData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label
                    >
                      {orderData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Grid>

            {/* Expenses Card */}
            <Grid item xs={12} sm={12} md={4}>
              <Card
                sx={{
                  p: 3,
                  height: "100%",
                  width: { xs: "300px", md: "400px" },
                }}
              >
                <Typography variant="subtitle1">Total Expenses:</Typography>
                <Typography variant="h5" fontWeight="bold">
                  $2,300
                </Typography>
                <Button size="small" sx={{ mt: 1, background: "#eaf2ff" }}>
                  This Month
                </Button>

                <ResponsiveContainer width="100%" height={isMobile ? 160 : 200}>
                  <PieChart>
                    <Pie
                      data={expenseData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      label
                    >
                      {expenseData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
